import { useCallback, useState, useEffect } from 'react';
import { formatCurrency } from '@atc/string-fns';
import { useDispatch, useSelector } from 'react-redux';
import _get from 'lodash/get';
import { useDevice } from '@atc/react-device';
import { eventBus } from '@atc/cai-event-bus';
import { getCreditTierOptions, getInterestRate, getObfuscatedCredit, calculateDownPayment } from '@atc/wallet-payment-fns';
import { Text, Row, Col, Select, PositionedOverlay, PositionedOverlayBase, DebouncedInput, PositionedOverlayAnchor, Badge, Image, Button, LoadingSpinner, Panel, Glyphicon, ItemCard, RadioGroup } from 'reaxl';
import { sendClick, DelayedImpressionObserver, sendImpressions } from 'reaxl-analytics';
import { myWalletInputChange, myWalletInteractionClick, myWalletGridTradeInIndicator, myWalletTradeInClickLink, walletPromoCardLinkClick, searchAvailableVehiclesClick, walletGridVerifyCreditClick, walletDropdownEngage } from 'reaxl-analytics-handlers';
import { useFeatures } from '@atc/features';
import { fallbackInterestRates, ratesLabels } from 'axl-config';
import { WALLET_CREATE_ID_REQUEST, WALLET_CREATE_ID_COMPLETED } from 'wallet-manager';
import clsx from 'clsx';
import WalletInput from './WalletInput.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import SelectButtonWrapper from '../SelectButtonWrapper/SelectButtonWrapper.js';
const REGISTERED_TRADEMARK = /*#__PURE__*/jsx("sup", {
  children: "\xAE"
});
const fetchTotalPayment = async _ref => {
  let {
    paymentsInfo,
    paymentCalculationFetcher
  } = _ref;
  const {
    loanTerm,
    monthlyBudget = 0,
    tradeIn,
    interestRate,
    downPayment,
    downPaymentPercent
  } = paymentsInfo;
  const tradeInParam = tradeIn?.value || 0;
  const amountOwedParam = tradeIn?.amountOwed || 0;
  const interestRateParam = getInterestRate(interestRate);
  const downPaymentParam = downPayment ? `downPayment=${downPayment}` : `downPaymentPercent=${downPaymentPercent * 100}`;
  const budgetApiParams = `monthlyPayment=${monthlyBudget}&tradeInValue=${tradeInParam}&amountOwed=${amountOwedParam}&loanTerm=${loanTerm}&interestRate=${interestRateParam}&${downPaymentParam}`;
  const {
    totalBudget
  } = (await paymentCalculationFetcher(budgetApiParams, true)) || {};
  return totalBudget;
};

// mapping to inputsMap on walletEvents.js
const inputsMap = {
  creditTier: 'creditType',
  downPayment: 'downPaymentAmount',
  monthlyBudget: 'budgetAmountInput',
  tradeIn: 'tradeInValue',
  amountOwed: 'amountOwed'
};
const DEFAULT_MAX_PAYMENT = 600;
function MyWalletInputCard(_ref2) {
  let {
    onSearchAvailVehiclesClick,
    paymentsDuck,
    paymentCalculationFetcher
  } = _ref2;
  const {
    credit_and_trade_in_popover_input: [enablePopoverSheet],
    credit_verification: [enableCreditVerification],
    global_kbb_branded_wallet: [, {
      hide_card_header: hideHeader,
      hide_kbb_branding: hideKbbBranding
    }],
    ico: [enableIco],
    wallet_grid_button_copy: [, {
      find_cars: enableFindCars,
      shop_by_budget: enableShopByBudget
    }],
    wallet_payoff_quote: [enablePayoffQuote]
  } = useFeatures(['credit_and_trade_in_popover_input', 'credit_verification', 'global_kbb_branded_wallet', 'ico', 'wallet_grid_button_copy', 'wallet_payoff_quote']);
  const device = useDevice();
  const isGreaterThanMd = _get(device, 'greaterThan.md', false);
  const dispatch = useDispatch();
  const updateMyWalletInteractions = useCallback(data => dispatch(paymentsDuck.creators.updateMyWalletInteractions(data)), [dispatch, paymentsDuck.creators]);
  const updateMyWallet = data => dispatch(paymentsDuck.creators.updateMyWallet(data));
  const updatePenciledLatestPageId = data => dispatch(paymentsDuck.creators.updatePenciledLatestPageId(data));
  const nationalRates = useSelector(state => state?.interestRates || fallbackInterestRates);
  const displayModal = useSelector(paymentsDuck.selectors.getDisplayMyWalletModal);
  const walletId = useSelector(paymentsDuck.selectors.getWalletId);
  const isPaymentsInfoLoaded = useSelector(paymentsDuck.selectors.getIsPaymentsInfoLoaded);
  const downPayment = useSelector(paymentsDuck.selectors.getDownPayment);
  const downPaymentPercent = useSelector(paymentsDuck.selectors.getDownPaymentPercent);
  const creditType = useSelector(paymentsDuck.selectors.getCreditType);
  const creditTierOptions = getCreditTierOptions(nationalRates);
  const decodedCreditType = ratesLabels[creditType]?.decodedValue || creditTierOptions[0].value;
  const budget = useSelector(paymentsDuck.selectors.getBudget);
  const monthlyBudget = useSelector(paymentsDuck.selectors.getMonthlyBudget);
  const loanTerm = useSelector(paymentsDuck.selectors.getLoanTerm);
  const selectButtonValue = creditTierOptions.find(option => option.value === decodedCreditType);
  const tradeIn = useSelector(paymentsDuck.selectors.getTradeIn);
  const {
    amountOwed,
    amountOwedSource,
    ico = [],
    hasNewTradeIn,
    value: tradeInValue
  } = tradeIn;
  const interestRates = useSelector(paymentsDuck.selectors.getInterestRates);
  const defaultInterestRate = getInterestRate(interestRates);
  const hasOpenedTradeIn = useSelector(state => paymentsDuck.selectors.getMyWalletInteraction(state, 'hasOpenedTradeIn'));
  const hasUpdatedWallet = useSelector(state => paymentsDuck.selectors.getMyWalletInteraction(state, 'hasUpdatedWallet'));
  const penciledLatestPageId = useSelector(paymentsDuck.selectors.getPenciledLatestPageId);
  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [hasInteractions, setHasInteractions] = useState(false);
  const [localDownPayment, setLocalDownPayment] = useState(0);
  const [defaultBudgetValue, setDefaultBudgetValue] = useState('');
  const displayBudgetValue = budget || defaultBudgetValue;
  const localMonthlyPayment = monthlyBudget || DEFAULT_MAX_PAYMENT;
  const getInterestRates = (type, term) => _get(nationalRates, `${type}.rates.${term}`, {});
  const showBadge = enableIco && !!ico.length && !hasOpenedTradeIn || hasNewTradeIn;
  const findOrCreateWalletId = async () => {
    if (!walletId) {
      await eventBus.emit(WALLET_CREATE_ID_REQUEST);
    }
  };
  const listenForWalletId = () => {
    eventBus.on(WALLET_CREATE_ID_COMPLETED);
    return () => {
      eventBus.off(WALLET_CREATE_ID_COMPLETED);
    };
  };
  const handleInputUpdate = useCallback(async (event, inputType) => {
    event.persist();
    const {
      value
    } = event.target;
    setIsSavingChanges(true);
    if (!walletId) {
      await findOrCreateWalletId();
      listenForWalletId();
    }
    const sanitizedValue = value.replace(/\D/g, '');
    let updatedWalletData = {};
    if (inputType === 'tradeIn' || inputType === 'amountOwed') {
      const tradeInDetails = {
        amountOwed,
        amountOwedSource,
        source: 'USER',
        ico,
        value: tradeInValue
      };
      updatedWalletData = {
        tradeIn: {
          ...tradeInDetails,
          ...(inputType === 'tradeIn' && {
            value: Number(sanitizedValue)
          }),
          ...(inputType === 'amountOwed' && {
            amountOwed: Number(sanitizedValue)
          })
        }
      };
    }
    if (inputType === 'downPayment') {
      updatedWalletData = {
        downPaymentPercent: null,
        downPayment: Number(sanitizedValue)
      };
    }
    if (inputType === 'monthlyBudget') {
      updatedWalletData = {
        monthlyBudget: Number(sanitizedValue)
      };
    }
    if (inputType === 'creditTier') {
      const obfuscatedCredit = getObfuscatedCredit(value);
      const newRates = getInterestRates(value, loanTerm);
      updatedWalletData = {
        creditType: obfuscatedCredit,
        interestRate: newRates
      };
    }
    dispatch(paymentsDuck.creators.updatePaymentsInfo(updatedWalletData));
    updateMyWalletInteractions({
      hasUpdatedWallet: true
    });
    setTimeout(() => {
      setIsSavingChanges(false);
    }, 1700);
    const extraData = inputType === 'creditTier' && ` - ${value}${ratesLabels[value]?.subLabel?.replace(/\s/g, '')}`;
    sendClick(myWalletInputChange, null, {
      inputName: inputsMap[inputType],
      walletPromoCardInput: true,
      paymentsDuck,
      ...(extraData && {
        extraData
      })
    });
    if (hasUpdatedWallet && !hasInteractions && penciledLatestPageId !== 'srp') {
      sendClick(myWalletInteractionClick, null, {
        paymentsDuck
      });
      setHasInteractions(true);
      updatePenciledLatestPageId('srp');
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [amountOwed, amountOwedSource, creditType, decodedCreditType, dispatch, ico, loanTerm, findOrCreateWalletId, getInterestRates, paymentsDuck.creators, sendClick, hasUpdatedWallet, hasInteractions, penciledLatestPageId]);
  const handleDropdownEngagement = label => {
    updateMyWalletInteractions({
      hasUpdatedWallet: true
    });
    if (hasUpdatedWallet && !hasInteractions && penciledLatestPageId !== 'srp') {
      sendClick(myWalletInteractionClick, null, {
        paymentsDuck
      });
      setHasInteractions(true);
      updatePenciledLatestPageId('srp');
    }
    sendClick(walletDropdownEngage, null, {
      label,
      paymentsDuck
    });
  };
  const creditClickHandler = () => {
    handleDropdownEngagement('credit-rating');
  };
  const tradeInClickHandler = () => {
    handleDropdownEngagement('trade-in');
  };
  const handleSearchAvailVehiclesClick = async maxPrice => {
    sendClick(searchAvailableVehiclesClick, null, {
      isWalletPromoCard: true,
      paymentsDuck
    });
    onSearchAvailVehiclesClick(maxPrice);
  };
  const onSearchVehiclesCtaClick = () => handleSearchAvailVehiclesClick(displayBudgetValue);
  const calculateDefaultBudget = async () => {
    const localPaymentsInfo = {
      loanTerm,
      monthlyBudget: localMonthlyPayment,
      tradeIn,
      interestRate: interestRates,
      downPayment,
      downPaymentPercent
    };
    const calculatedBudget = await fetchTotalPayment({
      paymentsInfo: localPaymentsInfo,
      paymentCalculationFetcher
    });
    setDefaultBudgetValue(calculatedBudget);
  };
  const calculateDisplayDownPayment = budgetValue => {
    const calculatedDownPayment = calculateDownPayment(budgetValue, {
      downPaymentPercent,
      downPayment
    });
    setLocalDownPayment(calculatedDownPayment || 0);
  };
  const calculateInitialValues = async () => {
    await calculateDefaultBudget();
    calculateDisplayDownPayment(defaultBudgetValue);
  };
  useEffect(() => {
    if (isPaymentsInfoLoaded && !downPayment && !budget) {
      calculateInitialValues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaymentsInfoLoaded, displayBudgetValue]);

  // recalculate display down payment when budget is adjusted
  useEffect(() => {
    if (!downPayment && budget) {
      calculateDisplayDownPayment(budget);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budget]);
  const onTradeInCtaClick = () => {
    sendClick(myWalletTradeInClickLink, null, {
      isWalletPromoCard: true,
      paymentsDuck
    });
    findOrCreateWalletId();
    if (!hasOpenedTradeIn) {
      updateMyWalletInteractions({
        hasOpenedTradeIn: true
      });
    }
    updateMyWallet({
      displayModal: !displayModal,
      selectedPanelName: 'kbbValuation'
    });
  };
  const onPayoffQuoteCtaClick = () => {
    handleDropdownEngagement('get_payoff_qt');
    updateMyWallet({
      displayModal: !displayModal,
      selectedPanelName: 'payoffQuote'
    });
  };
  const onKbbValuationClick = e => {
    findOrCreateWalletId();
    sendClick(walletPromoCardLinkClick, null, {
      text: `srp::${e.target.innerText}`,
      paymentsDuck
    });
    updateMyWallet({
      displayModal: !displayModal,
      selectedPanelName: 'valuationHistory'
    });
  };
  const onUpdateSettingsClick = e => {
    sendClick(walletPromoCardLinkClick, null, {
      text: e.target.textContent.trim(),
      paymentsDuck
    });
    updateMyWallet({
      displayModal: !displayModal
    });
  };
  const handleMonthlyPaymentChange = e => {
    handleInputUpdate(e, 'monthlyBudget');
  };
  const handleCreditTierChange = e => {
    handleInputUpdate(e, 'creditTier');
  };
  const handleDownPaymentChange = e => {
    handleInputUpdate(e, 'downPayment');
  };
  const handleTradeInChange = e => {
    handleInputUpdate(e, 'tradeIn');
  };
  const handleAmountOwedChange = e => {
    handleInputUpdate(e, 'amountOwed');
  };
  const handleTradeInImpression = () => {
    sendImpressions({
      name: myWalletGridTradeInIndicator,
      data: {
        isWalletPromoCard: true
      },
      paymentsDuck
    });
  };

  // using a text mask on input causes error on KBB so validating this way instead
  const numericValidation = event => {
    const objValue = event.target.value;
    const validatedNumber = objValue.replace(/[^0-9]/gi, '');
    event.target.value = validatedNumber;
  };
  const sharedInputProps = {
    maxLength: '10',
    type: 'text',
    layout: 'inside',
    onChangeDuration: 800,
    inputMode: 'numeric',
    onKeyUp: e => numericValidation(e)
  };
  const handleVerifyCreditClick = () => {
    sendClick(walletGridVerifyCreditClick);
    updateMyWallet({
      displayModal: true,
      selectedPanelName: 'creditVerification'
    });
  };
  const creditScoreSheet = /*#__PURE__*/jsxs("div", {
    className: "text-left",
    children: [/*#__PURE__*/jsx(RadioGroup, {
      onChange: handleCreditTierChange,
      name: "creditOptionsGroup",
      options: creditTierOptions,
      value: decodedCreditType
    }), enableCreditVerification && /*#__PURE__*/jsx(Button, {
      bsStyle: "text",
      onClick: handleVerifyCreditClick,
      children: "Verify My Credit"
    })]
  });
  const tradeInDisplayValue = tradeInValue ? `${formatCurrency(tradeInValue)}` : '$0';
  const tradeInAmountSupportingLink = enableIco && /*#__PURE__*/jsx(Button, {
    bsStyle: "text",
    className: "text-size-100 margin-bottom-1",
    "data-cmp": "kbbTradeInValuationCta",
    onClick: onTradeInCtaClick,
    children: `Get My Kelley Blue Book&#174; CarValue`
  });
  const amountOwedSupportingLink = enablePayoffQuote && /*#__PURE__*/jsx(Button, {
    bsStyle: "text",
    className: "text-size-100",
    "data-cmp": "payoffQuoteCta",
    onClick: onPayoffQuoteCtaClick,
    children: "Get My Payoff Quote"
  });
  const tradeInSheet = /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx("div", {
      className: "text-left",
      children: /*#__PURE__*/jsx(WalletInput, {
        ...sharedInputProps,
        formClassName: "margin-bottom-1",
        label: "Est. Trade-In Value",
        name: "popoverTradeInInput",
        onChange: handleTradeInChange,
        value: tradeInValue,
        supportingLink: tradeInAmountSupportingLink
      })
    }), /*#__PURE__*/jsx("div", {
      className: "text-left margin-top-4",
      children: /*#__PURE__*/jsx(WalletInput, {
        ...sharedInputProps,
        formClassName: "margin-bottom-1",
        label: "Amount Owed on Trade-In",
        name: "popoverTradeInInput",
        onChange: handleAmountOwedChange,
        value: amountOwed,
        supportingLink: amountOwedSupportingLink
      })
    })]
  });
  const walletBranding = /*#__PURE__*/jsxs(Text, {
    componentClass: "div",
    color: "primary",
    size: 200,
    weight: "bold",
    children: [/*#__PURE__*/jsxs("span", {
      className: "display-inline-block",
      children: [/*#__PURE__*/jsx(Glyphicon, {
        className: "text-size-200",
        glyph: "wallet"
      }), ' ', /*#__PURE__*/jsx(Text, {
        children: "My Wallet"
      })]
    }), ' ', !hideKbbBranding && /*#__PURE__*/jsxs(Text, {
      className: "display-inline-block",
      color: "subdued",
      weight: "normal",
      children: ["by Kelley Blue Book", REGISTERED_TRADEMARK]
    })]
  });
  const panelBody = /*#__PURE__*/jsxs("div", {
    className: "margin-top-1 display-flex flex-column",
    "data-cmp": "myWalletGridInputs",
    children: [hideHeader && /*#__PURE__*/jsx("div", {
      className: "margin-bottom-1",
      children: walletBranding
    }), /*#__PURE__*/jsx(Text, {
      color: "primary",
      componentClass: "div",
      size: 500,
      weight: "ultra-bold",
      children: "Find Your Vehicle Faster"
    }), /*#__PURE__*/jsx(Text, {
      className: "margin-bottom-4",
      color: "accent",
      componentClass: "div",
      size: 500,
      weight: "ultra-bold",
      children: "and Within Budget"
    }), /*#__PURE__*/jsxs(Row, {
      children: [/*#__PURE__*/jsx(Col, {
        xs: 6,
        sm: 12,
        children: /*#__PURE__*/jsx(WalletInput, {
          ...sharedInputProps,
          maxLength: 5,
          label: "Monthly Payment",
          name: "monthlyPaymentInput",
          onChange: handleMonthlyPaymentChange,
          value: localMonthlyPayment
        })
      }), /*#__PURE__*/jsxs(Col, {
        xs: 6,
        sm: 12,
        className: clsx({
          'margin-bottom-3': enablePopoverSheet
        }),
        children: [enablePopoverSheet && /*#__PURE__*/jsx(SelectButtonWrapper, {
          onClick: creditClickHandler,
          primaryLabel: "Credit Score",
          secondaryLabel: selectButtonValue.label,
          heading: "Credit Score",
          children: creditScoreSheet
        }), !enablePopoverSheet && /*#__PURE__*/jsx(Select, {
          id: "creditTypeSelect-wallet",
          labelProps: {
            label: 'Credit Score'
          },
          options: creditTierOptions,
          onChange: handleCreditTierChange,
          value: decodedCreditType,
          layout: "inside"
        })]
      })]
    }), /*#__PURE__*/jsxs(Row, {
      className: clsx({
        'margin-bottom-0 margin-bottom-sm-2': enablePopoverSheet
      }),
      children: [/*#__PURE__*/jsx(Col, {
        xs: 6,
        sm: 12,
        children: /*#__PURE__*/jsx(WalletInput, {
          ...sharedInputProps,
          label: "Down Payment",
          name: "downPaymentInput",
          onChange: handleDownPaymentChange,
          value: downPayment || localDownPayment
        })
      }), /*#__PURE__*/jsx(Col, {
        xs: 6,
        sm: 12,
        children: /*#__PURE__*/jsx(PositionedOverlay, {
          shrinkwrap: false,
          children: /*#__PURE__*/jsxs(PositionedOverlayBase, {
            children: [!enablePopoverSheet && /*#__PURE__*/jsx(DebouncedInput, {
              ...sharedInputProps,
              label: "Trade-In Value",
              name: "tradeInInput",
              onChange: handleTradeInChange,
              value: tradeInDisplayValue // If value is nullish, pass in '$0' to ensure the label still shows
            }), enablePopoverSheet && /*#__PURE__*/jsx(SelectButtonWrapper, {
              onClick: tradeInClickHandler,
              primaryLabel: "Trade-In Value",
              secondaryLabel: formatCurrency(tradeInValue - amountOwed ?? 0),
              heading: "Trade-In Value",
              children: tradeInSheet
            }), /*#__PURE__*/jsx(PositionedOverlayAnchor, {
              offsetRight: 50,
              offsetTop: 25,
              position: "topRight",
              children: showBadge && /*#__PURE__*/jsx(DelayedImpressionObserver, {
                onChange: handleTradeInImpression,
                delay: 0,
                children: /*#__PURE__*/jsx(Badge, {
                  circle: true,
                  children: "1"
                })
              })
            }, "tradeInOverlayAnchor")]
          }, "tradeInBadgeOverlayBase")
        })
      })]
    }), !hasNewTradeIn && enableIco && /*#__PURE__*/jsxs("div", {
      className: "display-flex justify-content-center align-items-center text-center",
      children: [isGreaterThanMd && !hideKbbBranding && /*#__PURE__*/jsx(Image, {
        alt: "",
        height: 25,
        src: "https://www.autotrader.com/cm-api/content/static/img/icon/logos/kbb-logo-notext.svg",
        width: 20
      }), /*#__PURE__*/jsx(Button, {
        bsStyle: "text",
        className: "text-size-100 line-height-sm margin-bottom-1",
        "data-cmp": "kbbTradeInValuationCta",
        onClick: onTradeInCtaClick,
        children: "Get My Kelley Blue Book\xAE Car Value"
      })]
    }), hasNewTradeIn && !hideKbbBranding && /*#__PURE__*/jsx(Button, {
      bsStyle: "text",
      className: "text-size-100",
      onClick: onKbbValuationClick,
      children: "Apply existing Kelley Blue Book trade-in value"
    }), /*#__PURE__*/jsx("div", {
      style: {
        height: '72px'
      },
      children: isSavingChanges ? /*#__PURE__*/jsx(LoadingSpinner, {
        className: "padding-top-3 margin-top-1"
      }) : /*#__PURE__*/jsxs("div", {
        children: [/*#__PURE__*/jsx(Text, {
          color: "primary",
          componentClass: "div",
          size: 650,
          weight: "ultra-bold",
          children: formatCurrency(displayBudgetValue)
        }), /*#__PURE__*/jsxs(Text, {
          className: "display-inline-block",
          color: "subdued",
          size: 100,
          children: ["Based on ", defaultInterestRate, "% APR for ", loanTerm, " mos."]
        }), /*#__PURE__*/jsx(Button, {
          bsStyle: "text",
          className: "text-size-100 display-inline-block margin-left-1",
          onClick: onUpdateSettingsClick,
          children: "Update Settings"
        })]
      })
    }), /*#__PURE__*/jsxs(Button, {
      block: true,
      bsStyle: "primary",
      className: "margin-top-2",
      disabled: isSavingChanges,
      onClick: onSearchVehiclesCtaClick,
      type: "submit",
      children: [enableFindCars && !enableShopByBudget && 'Find Affordable Cars', enableShopByBudget && 'Shop by Budget', !enableFindCars && !enableShopByBudget && 'Search Available Cars']
    })]
  });
  return /*#__PURE__*/jsxs(Panel, {
    bsStyle: "primary",
    "data-cmp": "myWalletGridPlacement",
    className: "padding-horizontal-0 text-center",
    children: [!hideHeader && /*#__PURE__*/jsx(Panel.Heading, {
      children: /*#__PURE__*/jsxs(Text, {
        componentClass: "div",
        size: 400,
        weight: "bold",
        children: [/*#__PURE__*/jsx(Glyphicon, {
          className: "text-size-500",
          glyph: "wallet"
        }), ' ', /*#__PURE__*/jsx(Text, {
          children: "My Wallet"
        }), ' ', !hideKbbBranding && /*#__PURE__*/jsxs(Text, {
          size: 200,
          weight: "normal",
          children: ["by Kelley Blue Book", REGISTERED_TRADEMARK]
        })]
      })
    }), /*#__PURE__*/jsx(Panel.Body, {
      className: "padding-bottom-0",
      children: /*#__PURE__*/jsx(ItemCard, {
        body: panelBody,
        uiContext: "portrait"
      })
    })]
  });
}
export { MyWalletInputCard as default };
